import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, skip } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PaginationComponent } from '../../inputs/pagination/pagination.component';
import { ContactsService } from '../../../services/contacts/contacts.service';
import { MessageService } from '../../../services/message/message.service';
import { GroupAudienceService } from '../../../services/segments/group-audience.service';
import { IOrganization, IPagination } from '../../../types/contactModule';
import { EditOrganizationFormComponent } from '../../forms/edit-organization-form/edit-organization-form.component';
import { PromptCardComponent } from '../../toasts-and-cards/prompt-card/prompt-card.component';
import { EmptyStateComponent } from '../../ui/empty-state/empty-state.component';
import { DataTableComponent } from '../../ui/data-table/data-table.component';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import { formatCount, formatWhen } from '../../../../utils/sms';
import { CountState, GroupSelection, countNoun, humanizeEnum } from '../group-table/group-rows';

interface OrganizationRow {
  organization: IOrganization;
  key: string;
  name: string;
  description: string | null;
  industry: string;
  place: string;
  updated: string;
  updatedTitle: string | null;
}

function toRows(organizations: IOrganization[]): OrganizationRow[] {
  return (organizations ?? [])
    .filter((org) => !!org && typeof org === 'object')
    .map((org, index) => {
      const stamp = org.modifiedAt || org.createdAt;
      const place = [org.city, org.country].map((part) => (part ?? '').toString().trim()).filter(Boolean);
      return {
        organization: org,
        key: org.uuid ? String(org.uuid) : org.id != null ? `id-${org.id}` : `row-${index}`,
        name: (org.name ?? '').toString().trim() || 'Untitled organization',
        description: (org.description ?? '').toString().trim() || null,
        industry: humanizeEnum(org.industry) || '—',
        place: place.join(', ') || '—',
        updated: formatWhen(stamp),
        updatedTitle: stamp ? new Date(stamp).toLocaleString('en-GB') : null,
      };
    });
}

@Component({
  selector: 'organizations-table',
  standalone: true,
  imports: [
    PaginationComponent,
    EditOrganizationFormComponent,
    PromptCardComponent,
    EmptyStateComponent,
    DataTableComponent,
    HasPermissionDirective,
    MatTooltipModule,
  ],
  templateUrl: './organizations-table.component.html',
  styleUrls: ['../group-table/group-table.css', './organizations-table.component.css'],
})
export class OrganizationsTableComponent implements OnInit, OnChanges {
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private messageService = inject(MessageService);
  private audience = inject(GroupAudienceService);
  contactService = inject(ContactsService);

  /** Search, sort and filters from the list page. A new object on each change. */
  @Input() params: Record<string, any> = {};
  @Input() searchTerm = '';
  @Input() filtered = false;
  @Output() getSelections: EventEmitter<IOrganization[]> = new EventEmitter();
  @Output() totalChange = new EventEmitter<number>();
  @Output() clearFilters = new EventEmitter<void>();

  readonly formatCount = formatCount;

  organizations: IOrganization[] = [];
  rows: OrganizationRow[] = [];
  counts: Record<string, CountState> = {};
  organizationPagination: IPagination = {
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  };

  loading = true;
  loadError = '';
  pageIndex = 0;
  pageSize = 10;
  private loadSub?: Subscription;
  private started = false;

  readonly selection = new GroupSelection<IOrganization>();

  showEditModal: boolean = false;
  showDeletePrompt: boolean = false;
  deleting = false;
  selectedOrganization: IOrganization | null = null;

  // ── Loading ──
  load() {
    this.started = true;
    this.loadSub?.unsubscribe();
    this.loading = true;
    this.loadError = '';
    this.loadSub = this.audience
      .listOrganizations({ ...this.params, page: this.pageIndex, size: this.pageSize })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          const pagination = page.itemPagination;
          const lastPage = Math.max(0, (pagination?.totalPages ?? 1) - 1);
          // Deleting the last rows of the last page: step back to the new last page.
          if (!page.items.length && this.pageIndex > lastPage && (pagination?.totalElements ?? 0) > 0) {
            this.pageIndex = lastPage;
            this.load();
            return;
          }
          this.organizations = page.items;
          this.rows = toRows(page.items);
          this.organizationPagination = { ...pagination, pageNumber: this.pageIndex };
          this.selection.refresh(page.items);
          this.loading = false;
          this.totalChange.emit(pagination?.totalElements ?? page.items.length);
          this.loadCounts();
        },
        error: () => {
          this.loading = false;
          this.loadError = 'Organizations did not load. Check your connection and try again.';
        },
      });
  }

  private loadCounts() {
    for (const row of this.rows) {
      const uuid = row.organization.uuid;
      if (!uuid || typeof this.counts[uuid] === 'number') continue;
      this.counts[uuid] = undefined;
      this.audience
        .memberCount({ kind: 'organization', uuid })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (count) => (this.counts = { ...this.counts, [uuid]: count }),
          error: () => (this.counts = { ...this.counts, [uuid]: null }),
        });
    }
  }

  countOf(organization: IOrganization | null): CountState {
    return organization?.uuid ? this.counts[organization.uuid] : null;
  }

  countState(organization: IOrganization): 'loading' | 'none' | 'known' {
    const count = this.countOf(organization);
    return count === undefined ? 'loading' : count === null ? 'none' : 'known';
  }

  // ── Paging ──
  goToPreviousPage() {
    this.updateOrganizatonPage(this.pageIndex - 1);
  }
  goToNextPage() {
    this.updateOrganizatonPage(this.pageIndex + 1);
  }
  updateOrganizatonPage(newPage: number) {
    this.pageIndex = Math.max(0, newPage);
    this.load();
  }
  /** The pager follows a size change with changePage(0), which loads. */
  onPageSizeChange(size: number) {
    this.pageSize = size;
  }

  // ── Selection ──
  selectOrganization(organization: IOrganization) {
    this.selection.toggle(organization);
    this.emitSelections();
  }

  toggleSelectAllOrganizations() {
    this.selection.togglePage(this.organizations);
    this.emitSelections();
  }

  deSelectAllOrganizations() {
    this.selection.clear();
    this.emitSelections();
  }

  get getIsAllSelected() {
    return this.selection.allOn(this.organizations);
  }

  get someOnPageSelected() {
    return this.selection.someOn(this.organizations);
  }

  getOrganizationIsSelected(organization: IOrganization) {
    return this.selection.has(organization);
  }

  emitSelections() {
    this.getSelections.emit(this.selection.items);
  }

  // ── Row actions ──
  goToOrganizationDetail(id: string) {
    if (id) this.router.navigate([`/organization/contacts/organizations/${id}`]);
  }

  openFromKeyboard(event: Event, organization: IOrganization) {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.goToOrganizationDetail(organization.uuid || '');
  }

  /** Opens the composer with this organization already picked. */
  messageOrganization(organization: IOrganization) {
    this.messageService.updateSelectedOrganizations([organization]);
    this.router.navigate(['/organization/messages/create']);
  }

  editContact(organization: IOrganization) {
    this.selectedOrganization = organization;
    this.showEditModal = true;
  }

  closeEditModal() {
    this.showEditModal = false;
  }

  openDeletePrompt(organization: IOrganization) {
    this.selectedOrganization = organization;
    this.showDeletePrompt = true;
  }

  closeDeletePrompt() {
    if (this.deleting) return;
    this.showDeletePrompt = false;
  }

  get deleteImpact(): string {
    const count = this.countOf(this.selectedOrganization);
    if (typeof count !== 'number') return 'Its contacts stay in your contact list.';
    if (count === 0) return 'It has no contacts.';
    return `${countNoun(count, 'contact', 'contacts')} leave this organization. They stay in your contact list.`;
  }

  /** Deletes through the organisation-scoped endpoint, one uuid at a time. */
  deleteOrganization() {
    const organization = this.selectedOrganization;
    if (!organization?.uuid || this.deleting) return;
    this.deleting = true;
    this.contactService.deleteSelectedOrganizations([organization.uuid]).subscribe({
      next: () => {
        this.deleting = false;
        this.selection.remove([organization]);
        this.emitSelections();
        this.showDeletePrompt = false;
        this.audience.invalidate({ kind: 'organization', uuid: organization.uuid! });
      },
      error: () => {
        this.deleting = false;
        this.showDeletePrompt = false;
        this.contactService.showError(`${organization.name || 'The organization'} was not deleted. Try again.`);
      },
    });
  }

  get contactLoading() {
    return this.contactService.isLoading;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['params']) {
      this.pageIndex = 0;
      this.load();
    }
  }

  ngOnInit() {
    if (!this.started) this.load();

    // ContactsService reloads its shared list after a create, edit or delete
    // anywhere in the app; reload this page with its own search and sort.
    this.contactService.getOrganizations
      .pipe(skip(1), debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
  }
}
